import { useState, type FormEvent } from "react";
import { leadCategories } from "@/mocks/leads";

type FormStatus = "idle" | "submitting" | "success" | "error";

interface FormResponseBody {
  code?: string;
  meta?: { message?: string; detail?: string };
  message?: string;
}

const FORM_ENDPOINT = "https://readdy.ai/api/form/dahga83oh653ivfvnu7g";
const MAX_LENGTH = 500;

export default function SubmitForm() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [formError, setFormError] = useState("");
  const [contentLength, setContentLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const guardValue = String(formData.get("website_alt") ?? "").trim();
    if (guardValue) {
      setStatus("success");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() === "") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponseBody | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponseBody;
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam =
        typeof serverMessage === "string" &&
        (serverMessage.includes("spam") || serverMessage.includes("form data is spam"));

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
        setContentLength(0);
      } else {
        setStatus("error");
        setFormError(serverMessage || "提交未能成功，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络连接异常，线索尚未提交，请稍后重试。");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-secondary-300 bg-secondary-50 p-6 md:p-8 text-center">
        <span className="mx-auto w-14 h-14 rounded-full bg-secondary-500 text-background-50 flex items-center justify-center">
          <i className="ri-check-line text-2xl" aria-hidden="true"></i>
        </span>
        <h3 className="mt-5 font-heading text-xl font-bold text-foreground-950">线索已收到，谢谢你的信任</h3>
        <p className="mt-3 text-sm text-foreground-600 leading-relaxed max-w-md mx-auto">
          编辑会在 3 个工作日内完成初步判断。如果你留下了邮箱，需要补充材料时我们会主动联系你；
          如果没有留，也不影响我们处理这条线索。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-foreground-950 text-background-50 text-sm font-label font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-add-line w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
          再投一条线索
        </button>
      </div>
    );
  }

  return (
    <form
      id="submit-lead-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
      noValidate={false}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <label htmlFor="lead_title" className="block text-sm font-label font-medium text-foreground-900 mb-2">
            线索标题 <span className="text-primary-600">*</span>
          </label>
          <input
            id="lead_title"
            name="lead_title"
            type="text"
            required
            maxLength={60}
            placeholder="一句话概括你想说的事，例如：某门店闭店前仍在推销年卡"
            className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
          />
        </div>

        <div>
          <label htmlFor="category" className="block text-sm font-label font-medium text-foreground-900 mb-2">
            线索类别 <span className="text-primary-600">*</span>
          </label>
          <select
            id="category"
            name="category"
            required
            defaultValue=""
            className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 cursor-pointer"
          >
            <option value="" disabled>
              请选择最接近的一项
            </option>
            {leadCategories.map((category) => (
              <option key={category.slug} value={category.name}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="region" className="block text-sm font-label font-medium text-foreground-900 mb-2">
            涉及地区（选填）
          </label>
          <input
            id="region"
            name="region"
            type="text"
            maxLength={40}
            placeholder="例如：江苏南京、线上平台"
            className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
          />
        </div>

        <div className="md:col-span-2">
          <label htmlFor="content" className="block text-sm font-label font-medium text-foreground-900 mb-2">
            线索内容 <span className="text-primary-600">*</span>
          </label>
          <textarea
            id="content"
            name="content"
            required
            maxLength={MAX_LENGTH}
            rows={6}
            onChange={(e) => setContentLength(e.target.value.length)}
            placeholder="时间、地点、人物、经过，想到多少写多少。不用讲文笔，也不要求字字确凿，我们会帮你梳理。"
            className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 resize-none"
          ></textarea>
          <div className="mt-1.5 flex items-center justify-between text-xs text-foreground-500 font-label">
            <span>最多 {MAX_LENGTH} 字，超出将无法提交</span>
            <span className={contentLength > MAX_LENGTH - 50 ? "text-primary-600" : ""}>
              {contentLength} / {MAX_LENGTH}
            </span>
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-label font-medium text-foreground-900 mb-2">
            联系邮箱（选填）
          </label>
          <input
            id="email"
            name="email"
            type="email"
            maxLength={80}
            placeholder="需要补充材料时，编辑会用它联系你"
            className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
          />
        </div>

        <div>
          <label htmlFor="publishable" className="block text-sm font-label font-medium text-foreground-900 mb-2">
            材料用途
          </label>
          <select
            id="publishable"
            name="publishable"
            defaultValue="仅核查，不建议公开"
            className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 cursor-pointer"
          >
            <option value="仅核查，不建议公开">仅核查，不建议公开</option>
            <option value="可匿名公开">可匿名公开</option>
            <option value="先由编辑判断">先由编辑判断</option>
          </select>
        </div>
      </div>

      <div className="addr-assist-note" aria-hidden="true">
        <label htmlFor="website_alt">备用联系渠道</label>
        <input
          id="website_alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
          aria-hidden="true"
        />
      </div>

      {status === "error" && (
        <p className="mt-5 flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line w-5 h-5 flex items-center justify-center shrink-0" aria-hidden="true"></i>
          <span>{formError || "提交未能成功，请稍后重试。"}</span>
        </p>
      )}

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === "submitting" ? (
            <>
              <i className="ri-loader-4-line w-4 h-4 flex items-center justify-center animate-spin" aria-hidden="true"></i>
              正在提交…
            </>
          ) : (
            <>
              <i className="ri-send-plane-fill w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
              匿名提交线索
            </>
          )}
        </button>
        <p className="text-xs text-foreground-500 leading-relaxed">
          提交即表示你同意我们在保护隐私的前提下核查该线索。本站不会要求你提供真实身份信息。
        </p>
      </div>
    </form>
  );
}